import React, { useReducer } from 'react';
import { Routes, Route, BrowserRouter } from 'react-router-dom';

import { DispatchContext } from '../../contexts/DispatchContext';
import { StateContext } from '../../contexts/StateContext';
import { initializer } from '../../reducer/initializer';
import { reducer } from '../../reducer/reducer';
import { Session } from '../../types/Session';
import { DataPage } from '../DataPage/DataPage';
import { LandingPage } from '../LandingPage/LandingPage';
import { LogPage } from '../LogPage/LogPage';
import { NotFound } from '../NotFound/NotFound';
import { ViewPage } from '../ViewPage/ViewPage';
import { ViewsPage } from '../ViewsPage/ViewsPage';
import { RequiredFields } from '../AdminPages/RequiredFields';
import { SlackSettings } from '../AdminPages/SlackSettings';
import { ConfirmReservationPage } from '../GuestPages/ConfirmReservationPage';
import EventStaff from '../AdminPages/EventStaff';
import { UserManagement } from '../AdminPages/UserManagement';
import { CreateNewPasswordPage } from '../LoginPage/CreateNewPasswordPage';
import DndProviderComponent from '../AdminPages/DndProviderComponent';
import { CheckManagement } from '../AdminPages/CheckManagement/CheckManagement';
import { LeftBar } from '../../components/SideBar/LeftBar';
import { AdminContainer } from '../AdminPages/AdminContainer';
import { Group } from '../General/Group';
import { Stack } from '@mui/material';
import { ActivityReservation } from '../General/ActivityReservation';
import { Activity } from '../General/Activity';
import { ServiceProvider } from '../General/ServiceProvider';
import { SalesPartner } from '../General/SalesPartner';
import { Accommodation } from '../General/Accommodation';
import { ServiceReservation } from '../General/ServiceReservation';

import { Toaster } from 'react-hot-toast';
import { Database } from '../AdminPages/DatabaseManagement/Database';
import { LockManagement } from '../AdminPages/LockManagement/LockManagement';
import { StagPlanner } from '../General/StagPlanner';
import { group } from '../../schema/group';
import { activityReservation } from '../../schema/activityReservation';
import { crazyGroup } from '../../schema/crazyGroup';
import { crazyBooking } from '../../schema/crazyBooking';

import {PendingChanges} from "../General/PendingChanges";
import { SalesPartnerPrices } from '../General/SalesPartnerPrices';
import { ReservationCheck } from '../General/ReservationCheck/ReservationCheck';
import { ReservationSummaries } from '../General/ReservationSummaries';
import {ProviderConfirmations} from "../General/ProviderConfirmations";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {TodoPage} from "../TodoPage/TodoPage";
import {TodoDueListener} from "../TodoPage/TodoDueListener";
import {ActivityGroups} from "../General/ActivityGroups";

interface Props {
  session: Session;
}

export const AuthenticatedPages: React.ComponentType<Props> = ({ session }) => {
  const [state, dispatch] = useReducer(reducer, session, initializer);

  const themeQuery = useTheme();
  const isMobile = useMediaQuery(themeQuery.breakpoints.down('lg'));

  // 🔴 új: Snoozed todos globális state + persist localStorage-ban
  const [snoozedTodos, setSnoozedTodos] = React.useState<number[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('snoozedTodos') || '[]');
    } catch {
      return [];
    }
  });

  React.useEffect(() => {
    localStorage.setItem('snoozedTodos', JSON.stringify(snoozedTodos));
  }, [snoozedTodos]);

  // 🔴 összevonjuk a reducer state-et és a snoozed state-et
  const combinedState = {
    ...state,
    snoozedTodos,
    setSnoozedTodos,
  };


  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={combinedState}>
        <BrowserRouter>
            <Stack
              sx={{
                flexDirection: isMobile ? 'column' : 'row',
                display: isMobile ? 'block' : '',
                height: '100vh',
              }}
            >
              <LeftBar changePage={() => {}} />

              <Routes>
                <Route path="views">
                  <Route path="new" element={<ViewsPage />} />
                  <Route path=":table">
                    <Route path="" element={<ViewPage />} />
                    <Route path="manage" element={<ViewsPage />} />
                  </Route>
                </Route>

                <Route path="data/:table" element={<DataPage />} />
                <Route path="data/:table/:id" element={<DataPage />} />

                <Route path="event-staff">
                  <Route path="scheduler" element={<EventStaff />} />
                </Route>
                <Route path="admin" element={<AdminContainer />}>
                  <Route path="user-management" element={<UserManagement />} />
                  <Route
                    path="check-management"
                    element={<CheckManagement />}
                  />
                  <Route path="database" element={<Database />} />
                  <Route
                    path="status-management"
                    element={<DndProviderComponent />}
                  />
                  <Route path="logs" element={<LogPage />} />
                  <Route path="required-fields" element={<RequiredFields />} />
                  <Route path="lock-management" element={<LockManagement />} />
                  <Route
                    path="reservation-summaries"
                    element={<ReservationSummaries />}
                  />
                  <Route
                    path="activity-groups"
                    element={<ActivityGroups />}
                  />
                </Route>
                <Route path="slack" element={<SlackSettings />} />
                <Route
                  path="service-provider-confirmation/:id"
                  element={<ConfirmReservationPage />}
                />
                <Route
                  path="password-reset/:passwordResetKey"
                  element={<CreateNewPasswordPage />}
                />
                <Route
                  path="groups/:id?"
                  element={
                    <Group
                      key="groups"
                      schemaProvider={() => {
                        return group;
                      }}
                      subSchemaProvider={() => {
                        return activityReservation();
                      }}
                      viewName="groups"
                      subViewName="activity_reservations"
                      baseUrl="/groups"
                    />
                  }
                />
                <Route path="activity-reservations">
                  <Route path="" element={<ActivityReservation />} />
                  <Route path=":id" element={<ActivityReservation />} />
                </Route>
                <Route path="service-reservations">
                  <Route path="" element={<ServiceReservation />} />
                  <Route path=":id" element={<ServiceReservation />} />
                </Route>
                <Route path="activities">
                  <Route path="" element={<Activity />} />
                  <Route path=":id" element={<Activity />} />
                </Route>
                <Route path="service-providers">
                  <Route path="" element={<ServiceProvider />} />
                  <Route path=":id" element={<ServiceProvider />} />
                </Route>
                <Route path="sales-partners">
                  <Route path="" element={<SalesPartner />} />
                  <Route path=":id" element={<SalesPartner />} />
                </Route>
                <Route path="accommodations">
                  <Route path="" element={<Accommodation />} />
                  <Route path=":id" element={<Accommodation />} />
                </Route>
                <Route
                  path="crazy-groups/:id?"
                  element={
                    <Group
                      key="crazy-groups"
                      schemaProvider={() => {
                        return crazyGroup;
                      }}
                      subSchemaProvider={() => {
                        return crazyBooking;
                      }}
                      viewName="crazy-groups"
                      subViewName="crazy-bookings"
                      baseUrl="/crazy-groups"
                    />
                  }
                />
                <Route path="" element={<LandingPage />} />
                <Route path="stag-planner" element={<StagPlanner />} />
                <Route
                  path="reservation-check"
                  element={<ReservationCheck />}
                />
                <Route path="sales-partner-prices" element={<AdminContainer />}>
                  <Route path="" element={<SalesPartnerPrices />} />
                </Route>
                <Route path="pending-changes" element={<AdminContainer />}>
                  <Route
                    path=""
                    element={<PendingChanges />}
                  />
                </Route>
                <Route path="provider-confirmations" element={<AdminContainer />}>
                  <Route
                    path=""
                    element={<ProviderConfirmations />}
                  />
                </Route>
                <Route path="remainders" element={<AdminContainer />}>
                  <Route
                    path=""
                    element={<TodoPage />}
                  />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>

              {/* 🔔 Listener, ami figyeli az esedékes Todo-kat */}
              <TodoDueListener />

            </Stack>
            <Toaster position="bottom-center" />
          </BrowserRouter>
      </StateContext.Provider>
    </DispatchContext.Provider>
  );
};
